import { useState, useCallback } from 'react';
import { Member } from '@/types/member';

interface UsePhotoZoomReturn {
  isZoomOpen: boolean;
  selectedMember: Member | null;
  currentIndex: number;
  openZoom: (member: Member, allMembers?: Member[], index?: number) => void;
  closeZoom: () => void;
  navigateToNext: () => void;
  navigateToPrev: () => void;
}

export const usePhotoZoom = (allMembers?: Member[]): UsePhotoZoomReturn => {
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openZoom = useCallback((member: Member, members?: Member[], index?: number) => {
    setSelectedMember(member);
    if (members && index !== undefined) {
      setCurrentIndex(index);
    } else if (allMembers) {
      const foundIndex = allMembers.findIndex(m => m.id === member.id);
      setCurrentIndex(foundIndex >= 0 ? foundIndex : 0);
    } else {
      setCurrentIndex(0);
    }
    setIsZoomOpen(true);
  }, [allMembers]);

  const closeZoom = useCallback(() => {
    setIsZoomOpen(false);
    setSelectedMember(null);
  }, []);

  const navigateToNext = useCallback(() => {
    if (!allMembers || allMembers.length <= 1) return;
    
    const nextIndex = (currentIndex + 1) % allMembers.length;
    setCurrentIndex(nextIndex);
    setSelectedMember(allMembers[nextIndex]);
  }, [allMembers, currentIndex]);

  const navigateToPrev = useCallback(() => {
    if (!allMembers || allMembers.length <= 1) return;
    
    const prevIndex = currentIndex === 0 ? allMembers.length - 1 : currentIndex - 1;
    setCurrentIndex(prevIndex);
    setSelectedMember(allMembers[prevIndex]);
  }, [allMembers, currentIndex]);

  return {
    isZoomOpen,
    selectedMember,
    currentIndex,
    openZoom,
    closeZoom,
    navigateToNext,
    navigateToPrev
  };
};